import React, { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import EpmTooltip from "../EpmTooltip";
import { useEPMContext } from "../../../../contexts/EPMContext";
import { useUserContext } from "../../../../contexts/UserContext";
import { permission } from "../../../../config/permission";
import { notifyAutoSaved } from "../useAutoSave";
import { iceScoreSchema } from "../../validation/epmSchemas";

export const ICE_GUIDELINES = {
    Impact: [
        { min: 9, max: 10, range: "9-10", text: "Primary metric, highest-traffic step, large visible change" },
        { min: 7, max: 8, range: "7-8", text: "Directly moves a primary conversion metric on a high-traffic step" },
        { min: 5, max: 6, range: "5-6", text: "Primary metric on a mid-traffic step, or a secondary metric on a high-traffic step" },
        { min: 3, max: 4, range: "3-4", text: "Secondary metric or a low-traffic step" },
        { min: 1, max: 2, range: "1-2", text: "Cosmetic change, no expected metric movement" },
    ],
    Confidence: [
        { min: 9, max: 10, range: "9-10", text: "A past test on this property won on the same hypothesis" },
        { min: 7, max: 8, range: "7-8", text: "Strong analytics evidence and a related win on this site" },
        { min: 5, max: 6, range: "5-6", text: "One solid evidence source (analytics, research or heuristic review)" },
        { min: 3, max: 4, range: "3-4", text: "Competitor example or best-practice article only" },
        { min: 1, max: 2, range: "1-2", text: "Opinion, no evidence attached" },
    ],
    Ease: [
        { min: 9, max: 10, range: "9-10", text: "Styling change inside an existing Experience" },
        { min: 7, max: 8, range: "7-8", text: "New content or component, no new tracking" },
        { min: 5, max: 6, range: "5-6", text: "New component plus new tracking or a data dependency" },
        { min: 3, max: 4, range: "3-4", text: "Needs developer work outside the test tool" },
        { min: 1, max: 2, range: "1-2", text: "Needs back-end or platform change" },
    ],
};

const getScoreText = (category, score) => {
    const list = ICE_GUIDELINES[category];
    if (!list) return "";
    const found = list.find((item) => score >= item.min && score <= item.max);
    return found ? found.text : "";
};

const renderTooltipContent = (category, currentScore) => {
    const list = ICE_GUIDELINES[category] || [];
    return (
        <div className="flex flex-col gap-1.5 py-0.5 text-[12px] min-w-[300px]">
            <div className="font-semibold text-white/95 pb-1 border-b border-white/15 mb-0.5">
                {category} Scoring Guide
            </div>
            {list.map((tier) => {
                const isSelected = currentScore >= tier.min && currentScore <= tier.max;
                return (
                    <div
                        key={tier.range}
                        className={`flex items-start gap-2.5 px-2 py-1.5 rounded transition-all ${
                            isSelected
                                ? "bg-white/20 text-white font-bold ring-1 ring-white/30"
                                : "text-[#cdd4e7] font-normal"
                        }`}
                    >
                        <span className={`min-w-[36px] tracking-tight ${isSelected ? "text-white font-bold" : "text-[#8ea0c7] font-medium"}`}>
                            {tier.range}
                        </span>
                        <span className="leading-snug">{tier.text}</span>
                    </div>
                );
            })}
        </div>
    );
};

const SCORES = Array.from({ length: 10 }, (_, index) => 10 - index);

// Score picker: each score is listed with the sentence it stands for, so the
// scorer picks the sentence that fits rather than a bare number (storyboard 02)
const ScoreSelect = ({ category, value, onChange, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [active, setActive] = useState(value);
    const wrapRef = useRef(null);
    const listId = `priority-${category}-options`;

    useEffect(() => {
        if (!isOpen) return undefined;
        const close = (event) => {
            if (wrapRef.current && !wrapRef.current.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [isOpen]);

    const open = () => {
        if (disabled) return;
        setActive(value);
        setIsOpen(true);
    };
    const pick = (score) => {
        setIsOpen(false);
        if (score !== value) onChange(score);
    };

    const handleKeyDown = (event) => {
        if (!isOpen) {
            if (["ArrowDown", "ArrowUp", "Enter", " "].includes(event.key)) {
                event.preventDefault();
                open();
            }
            return;
        }
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((score) => Math.max(1, score - 1));
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((score) => Math.min(10, score + 1));
        } else if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            pick(active);
        } else if (event.key === "Escape" || event.key === "Tab") {
            // Stop the workflow page reacting to Escape while the list is open
            if (event.key === "Escape") event.stopPropagation();
            setIsOpen(false);
        }
    };

    return (
        <div className="epm-priority__score-select" ref={wrapRef}>
            <button
                type="button"
                id={`priority-${category}`}
                className={`epm-priority__score-trigger${isOpen ? " is-open" : ""}`}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                aria-controls={listId}
                aria-activedescendant={isOpen ? `${listId}-${active}` : undefined}
                disabled={disabled}
                onClick={() => (isOpen ? setIsOpen(false) : open())}
                onKeyDown={handleKeyDown}
            >
                <span className="epm-priority__score-num">{value}<ChevronDown size={14} /></span>
                <span className="epm-priority__score-text">{getScoreText(category, value)}</span>
            </button>
            {isOpen && (
                <ul className="epm-priority__score-options" id={listId} role="listbox" aria-label={`${category} score`}>
                    {SCORES.map((score) => {
                        const band = ICE_GUIDELINES[category].find((item) => score >= item.min && score <= item.max);
                        return (
                            <li
                                key={score}
                                id={`${listId}-${score}`}
                                role="option"
                                aria-selected={score === value}
                                className={[
                                    score === value ? "is-selected" : "",
                                    score === active ? "is-active" : "",
                                    band && score === band.max && score !== 10 ? "is-band-start" : "",
                                ].join(" ").trim()}
                                onMouseEnter={() => setActive(score)}
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => pick(score)}
                            >
                                <b>{score}</b>
                                <span>{band?.text}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const ICEScore = ({ scores, setScores, iceScore, testData, setTestData }) => {
    const { updateTest } = useEPMContext();
    const { checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);

    const handleScoreChange = async (name, newScore) => {
        if (!canUpdate) return;
        const numScore = Number(newScore);
        try {
            iceScoreSchema.validateSync(numScore);
        } catch (err) {
            console.error('Invalid ICE score:', err);
            return;
        }

        const newScores = {
            ...scores,
            [name]: numScore,
        };
        setScores(newScores);

        const scorePayload = {
            impact: Number(newScores.Impact),
            confidence: Number(newScores.Confidence),
            ease: Number(newScores.Ease),
        };

        if (setTestData) {
            setTestData((prev) => ({
                ...prev,
                priority: { ...prev?.priority, ...scorePayload },
            }));
        }

        const testId = testData?._id || testData?.id;
        if (testId && updateTest) {
            try {
                await updateTest(testId, { priority: scorePayload });
                notifyAutoSaved();
            } catch (err) {
                console.error('Failed to update test ICE scores:', err);
            }
        }
    };

    return (
        <section className="epm-priority__score-card relative">
            <h3>ICE score — pick the sentence that fits</h3>
            <div className="epm-priority__score-layout">
                <div className="epm-priority__scores">
                    {Object.keys(ICE_GUIDELINES).map((name) => (
                        <div
                            className="epm-priority__score-row relative"
                            key={name}
                            id={`epm-field-${name.toLowerCase()}`}
                        >
                            <label htmlFor={`priority-${name}`}>{name}</label>
                            <div className="epm-priority__select-wrap">
                                <ScoreSelect
                                    category={name}
                                    value={scores[name]}
                                    onChange={(newScore) => handleScoreChange(name, newScore)}
                                    disabled={!canUpdate}
                                />
                                <EpmTooltip content={renderTooltipContent(name, scores[name])} />
                            </div>
                            {name === "Confidence" && (
                                <p>
                                    Suggested 7 from the attached evidence · you chose {scores.Confidence}
                                    <EpmTooltip content={"Stride suggests a Confidence level from the evidence on the idea and matching past tests. You can override it; both values are stored, so score inflation can be measured."}/>
                                </p>
                            )}
                        </div>
                    ))}
                </div>
                <div className="epm-priority__ice">
                    <strong>{iceScore}</strong>
                    <span>
                        ICE{" "}
                        <EpmTooltip content="Calculated as (Impact + Confidence + Ease) / 3" />
                    </span>
                </div>
            </div>
        </section>
    );
};

export default ICEScore;

